/**
 * Types for the recommendations module
 *
 * This module defines types specific to the recommendation system
 * without modifying existing search-core contracts.
 */

import { ProductResult } from "../types/ProductResult";

/**
 * Seed query for recommendation discovery
 * Represents a search query used to fetch recommendation candidates
 */
export interface RecommendationSeed {
  /** The search query string */
  query: string;
  /** Priority level (1: personalized, 2: fallback) */
  priority: number;
  /** Source of the seed query */
  source: "personalized" | "fallback";
}

/**
 * Configuration for the recommendation service
 */
export interface RecommendationConfig {
  /** Maximum number of seed queries to use (3-5) */
  maxSeedQueries: number;
  /** Maximum products to fetch per seed query */
  maxProductsPerQuery: number;
  /** Maximum total products in final recommendations (5-10) */
  maxTotalProducts: number;
  /** Time-to-live for cache in milliseconds (default: 1 hour) */
  cacheTTL: number;
}

/**
 * Cache structure for recommendations
 */
export interface RecommendationCache {
  /** Cached product recommendations */
  products: ProductResult[];
  /** Timestamp when cache was created */
  timestamp: number;
  /** Time-to-live in milliseconds */
  ttl: number;
}

/**
 * Default configuration for recommendations
 */
export const DEFAULT_RECOMMENDATION_CONFIG: RecommendationConfig = {
  maxSeedQueries: 5,
  maxProductsPerQuery: 100,
  maxTotalProducts: 8,
  cacheTTL: 60 * 60 * 1000, // 1 hour in milliseconds
};

/**
 * Fallback seed queries when no personalized seeds are available
 * These represent popular categories or high-interest products
 */
export const FALLBACK_SEED_QUERIES: RecommendationSeed[] = [
  { query: "аппликаторы Euronda", priority: 2, source: "fallback" },
  { query: "тоталцем цемент", priority: 2, source: "fallback" },
  { query: "Цельтра Пресс LT, Dentsply", priority: 2, source: "fallback" },
  { query: "Нео Спектра ST HV", priority: 2, source: "fallback" },
  { query: "Альвостаз губка, 30шт", priority: 2, source: "fallback" },
  { query: "801 314", priority: 2, source: "fallback" },
];

/**
 * LocalStorage keys used by the recommendations module
 */
export const STORAGE_KEYS = {
  /** Key for personalized seed queries in localStorage */
  SEED_QUERIES: "search.recommendations.seedQueries",
  /** Key for recommendation cache in localStorage */
  CACHE: "search.recommendations.cache",
} as const;
